/**
 * Invite link by voice (/#/skraning/<token>): shows the section (and title) the link is for; the new
 * member enters name, e-mail, phone and a password, the account is created and signed in, the token
 * leaves the URL and — when running in the browser — the Home-Screen guide shows before onboarding
 * (photo, sharing consent, language). Rendered outside the router.
 */
import { useQuery } from '@tanstack/react-query';
import { TriangleAlert, UserPlus } from 'lucide-react';
import { useState, type FormEvent, type KeyboardEvent } from 'react';
import type { InspectSignupResponse } from '../../../shared/dto/auth';
import { normalizeEmail } from '../../../shared/email';
import { inspectSignup, signup } from '../../api/auth';
import { ApiFailure } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { Button, Card, ErrorBox, Spinner, TextInput } from '../../components/ui';
import { useT } from '../../i18n';
import { isStandalone } from '../../lib/standalone';
import { PrivacyLink } from '../privacy/PrivacyLink';
import { InstallStep, InvalidLink, isInvalidToken } from './AcceptTokenScreen';
import { AuthBrand, AuthLayout } from './AuthLayout';
import { apiErrorText } from './errors';
import { EMPTY_NEW_PASSWORD, NewPasswordFields, newPasswordOk } from './NewPasswordFields';
import { NAME_MAX, rejectedFields, signupInvalid, type SignupFields } from './validation';

/** The keyboard's "next" key is a plain Enter: move to the next field instead of submitting the form. */
function nextOnEnter(e: KeyboardEvent<HTMLInputElement>) {
  if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
  const inputs = Array.from(e.currentTarget.form?.querySelectorAll<HTMLInputElement>('input:not([type="hidden"])') ?? []);
  const next = inputs[inputs.indexOf(e.currentTarget) + 1];
  if (!next) return;
  e.preventDefault();
  next.focus();
}

function SignupForm({ token, info, onDone, onInvalid }: { token: string; info: InspectSignupResponse; onDone: () => void; onInvalid: () => void }) {
  const t = useT();
  const auth = useAuth();
  const [fields, setFields] = useState<SignupFields>({ fullName: '', email: '', phone: '' });
  const [pw, setPw] = useState(EMPTY_NEW_PASSWORD);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The (normalised) e-mail the server said already has an account. */
  const [takenEmail, setTakenEmail] = useState<string | null>(null);
  /** Fields the server rejected; cleared as soon as that field is edited. */
  const [serverInvalid, setServerInvalid] = useState<ReadonlySet<string>>(new Set());

  const invalid = signupInvalid(fields);
  const taken = takenEmail !== null && normalizeEmail(fields.email) === takenEmail;
  const bad = (k: keyof SignupFields) => (tried && !!invalid[k]) || serverInvalid.has(k);
  const otherUser = auth.status === 'signed-in' ? auth.me.fullName : null;

  const set = (k: keyof SignupFields, v: string) => {
    setError(null);
    setFields((f) => ({ ...f, [k]: v }));
    if (serverInvalid.has(k)) setServerInvalid((s) => new Set([...s].filter((x) => x !== k)));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setTried(true);
    if (Object.keys(invalid).length > 0 || taken || !newPasswordOk(pw)) return;
    setError(null);
    setBusy(true);
    try {
      await signup(token, { fullName: fields.fullName.trim(), email: fields.email, phone: fields.phone.trim() || null, password: pw.pw }, info.kdfV);
      onDone();
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiFailure && err.code === 'conflict' && err.message === 'email_taken') {
        setTakenEmail(normalizeEmail(fields.email));
        return;
      }
      if (err instanceof ApiFailure && err.code === 'validation') {
        const rejected = [...rejectedFields(err.details)].filter((k) => k === 'fullName' || k === 'email' || k === 'phone');
        if (rejected.length > 0) {
          setServerInvalid(new Set(rejected));
          return;
        }
      }
      if (isInvalidToken(err)) onInvalid();
      else setError(apiErrorText(t, err));
    }
  };

  return (
    <AuthLayout>
      <Card className="px-5 py-7 md:px-8 md:py-9">
        <AuthBrand />
        <div className="mt-6 mb-5 flex flex-col items-center gap-2 text-center">
          <span className="flex size-14 items-center justify-center rounded-full bg-[var(--accent-soft)] text-[var(--accent)]">
            <UserPlus className="size-7" aria-hidden />
          </span>
          <h1 className="text-2xl font-bold tracking-tight">{t('auth.signup.title')}</h1>
          <p className="text-[var(--ink-2)]">{t('auth.signup.intro')}</p>
          <dl className="mt-2 flex flex-wrap justify-center gap-2 text-[16px]">
            <div className="flex gap-1.5 rounded-full bg-[var(--accent-soft)] px-3.5 py-1.5">
              <dt className="text-[var(--ink-2)]">{t('auth.signup.voice')}:</dt>
              <dd className="font-semibold text-[var(--accent)]">{t(`section.${info.section}`)}</dd>
            </div>
            {info.title ? (
              <div className="flex gap-1.5 rounded-full bg-[var(--accent-soft)] px-3.5 py-1.5">
                <dt className="text-[var(--ink-2)]">{t('auth.signup.title_label')}:</dt>
                <dd className="font-semibold text-[var(--accent)]">{info.title}</dd>
              </div>
            ) : null}
          </dl>
        </div>

        {otherUser ? (
          <p className="mb-4 flex items-start gap-3 rounded-xl bg-amber-100 px-4 py-3 text-amber-950 dark:bg-amber-900/40 dark:text-amber-100">
            <TriangleAlert className="mt-0.5 size-5 shrink-0" aria-hidden />
            <span>{t('auth.accept.other_user', { name: otherUser })}</span>
          </p>
        ) : null}

        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate method="post">
          <TextInput
            label={t('auth.signup.name')}
            name="name"
            autoComplete="name"
            autoCapitalize="words"
            enterKeyHint="next"
            maxLength={NAME_MAX}
            value={fields.fullName}
            onChange={(e) => set('fullName', e.target.value)}
            onKeyDown={nextOnEnter}
            readOnly={busy}
            error={bad('fullName') ? t('auth.signup.name_required') : null}
          />
          <TextInput
            label={t('auth.signup.email')}
            hint={t('auth.signup.email_hint')}
            type="email"
            name="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            maxLength={254}
            value={fields.email}
            onChange={(e) => set('email', e.target.value)}
            onKeyDown={nextOnEnter}
            readOnly={busy}
            error={taken ? t('auth.signup.email_taken') : bad('email') ? t('auth.signup.email_invalid') : null}
          />
          <TextInput
            label={t('auth.signup.phone')}
            type="tel"
            name="tel"
            inputMode="tel"
            autoComplete="tel"
            enterKeyHint="next"
            maxLength={24}
            value={fields.phone}
            onChange={(e) => set('phone', e.target.value)}
            onKeyDown={nextOnEnter}
            readOnly={busy}
            error={bad('phone') ? t('auth.onb.phone_invalid') : null}
          />
          <NewPasswordFields value={pw} onChange={setPw} tried={tried} busy={busy} />
          {error ? <ErrorBox>{error}</ErrorBox> : null}
          <Button type="submit" size="lg" block icon={UserPlus} busy={busy} className="mt-1">
            {busy ? t('auth.signup.busy') : t('auth.signup.submit')}
          </Button>
        </form>
        <div className="mt-4 flex justify-center">
          <PrivacyLink />
        </div>
      </Card>
    </AuthLayout>
  );
}

export default function SignupScreen({ token }: { token: string }) {
  const t = useT();
  // 'leaving': the account exists and the app is being reloaded (installed app) — terminal.
  const [phase, setPhase] = useState<'form' | 'invalid' | 'install' | 'leaving'>('form');
  const q = useQuery({
    queryKey: ['auth-signup', token],
    queryFn: () => inspectSignup(token),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    refetchOnWindowFocus: false,
    networkMode: 'always',
    // Signing in can wipe a previous user's queries; never re-inspect the now used link.
    enabled: phase === 'form',
  });

  if (phase === 'leaving') {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-6 py-10">
          <AuthBrand />
          <Spinner label={t('common.loading')} />
        </div>
      </AuthLayout>
    );
  }
  if (phase === 'install') return <InstallStep title={t('auth.signup.done_title')} />;
  if (phase === 'invalid' || (q.isError && isInvalidToken(q.error))) return <InvalidLink />;

  if (q.isPending) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-6 py-10">
          <AuthBrand />
          <Spinner label={t('auth.accept.checking')} />
        </div>
      </AuthLayout>
    );
  }

  if (q.isError) {
    return (
      <AuthLayout>
        <Card className="flex flex-col gap-5 px-6 py-8">
          <AuthBrand />
          <ErrorBox onRetry={() => void q.refetch()} retryLabel={t('common.retry')}>
            {apiErrorText(t, q.error)}
          </ErrorBox>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <SignupForm
      token={token}
      info={q.data}
      onInvalid={() => setPhase('invalid')}
      onDone={() => {
        // Leave the form first (see AcceptTokenScreen), then drop the single-use token from the URL.
        const standalone = isStandalone();
        setPhase(standalone ? 'leaving' : 'install');
        window.history.replaceState(null, '', '/');
        if (standalone) window.location.replace('/');
      }}
    />
  );
}
